/* global $ */
import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import { get_token, getCookie } from '@/router/index.js';
import Swal from 'sweetalert2';

export default {
  name: 'RolePermissionsView',
  components: { Sidebar, Navbar },

  data: () => ({
    csrfToken: '',
    dataTable: null,

    role_id: null,
    role_name: '',
    role_description: '',

    role_permissions: [],
    available_permissions: [],

    permissions_loading: false,
    modal_loading: false,
    permission_search: '',
    selected_permissions: [],

    show_assign_modal: false,
    show_details_modal: false,
    selected_permission_details: null,
    returning_to_assign_modal: false,

    //role users managemen
    role_users: [],
    available_users: [],
    users_loading: false,
    show_user_modal: false,
    user_search: '',
    selected_user_ids: [],
  }),

  computed: {
    // counts for stats
    total_assigned_count() {
      return this.role_permissions.length;
    },
    active_permissions_count() {
      return this.role_permissions.filter(p => !!p.is_active).length;
    },
    inactive_permissions_count() {
      return this.role_permissions.filter(p => !p.is_active).length;
    },

    // numeric assigned IDs as array + Set (for quick lookups)
    assigned_permission_ids() {
      return this.role_permissions.map(p => Number(p.id));
    },
    assignedIdSet() {
      return new Set(this.role_permissions.map(p => Number(p.id)));
    },

    // permissions not already assigned
    unassigned_permissions() {
      return this.available_permissions.filter(p => !this.assignedIdSet.has(Number(p.id)));
    },

    filtered_unassigned_permissions() {
      if (!this.permission_search) return this.unassigned_permissions;
      const search = this.permission_search.toLowerCase();
      return this.unassigned_permissions.filter(p => {
        const name_match = (p.name || '').toLowerCase().includes(search);
        const desc_match = (p.description || '').toLowerCase().includes(search);
        const features_match = p.main_features && p.main_features.some(f =>
          f.toLowerCase().includes(search)
        );
        return name_match || desc_match || features_match;
      });
    },

    total_users_count() {
      return this.role_users.length;
    },
    active_users_count() {
      return this.role_users.filter(user => user.is_active === true).length;
    },
    inactive_users_count() {
      return this.role_users.filter(user => user.is_active === false).length;
    },
  },

  mounted() {
    this.csrfToken = getCookie('csrftoken');
    // accept either /:id or /:roleId
    this.role_id = Number(this.$route.params.id || this.$route.params.roleId);

    if (!this.role_id) {
      this.$notyf?.error('Invalid role ID');
      this.$router.back();
      return;
    }

    // Load data
    this.load_role_data();
  },

  methods: {
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },

    initialize_data_table() {
      this.$nextTick(() => {
        const $table = $('#permissions_table');
        if (!$table.length || !$.fn.DataTable) return;
        if ($.fn.DataTable.isDataTable($table)) {
          $table.DataTable().destroy();
        }
        this.dataTable = $table.DataTable({
          pageLength: 10,
          lengthChange: false,
          searching: true,
          ordering: true,
          paging: true,
          info: true,
          autoWidth: false
        });
      });
    },

    initialize_user_data_table() {
      this.$nextTick(() => {
        const $table = $('#users_table');
        if (!$table.length || !$.fn.DataTable) return;
        if ($.fn.DataTable.isDataTable($table)) {
          $table.DataTable().destroy();
        }
        this.dataTable = $table.DataTable({
          pageLength: 10,
          lengthChange: false,
          searching: true,
          ordering: true,
          paging: true,
          info: true,
          autoWidth: false
        });
      });
    },

    navigate_to_permissions(user) {
      this.$router.push({
        name: 'UserPermissions',
        params: { user_id: user.id },
      });
    },

    normalize_permission_data: (pd = {}) => ({
      id: pd.id ?? null,
      name: pd.name ?? '',
      description: pd.description ?? '',
      is_active: pd.is_active ?? true,
      main_features: Array.isArray(pd.main_features) ? pd.main_features : [],
      main_features_detailed: Array.isArray(pd.main_features_detailed) ? pd.main_features_detailed : []
    }),

    async load_role_data() {
      this.permissions_loading = true;
      this.users_loading = true;
      try {
        await Promise.all([
          this.get_role_details(),
          this.get_role_permissions(),
          this.get_all_permissions(),
          this.get_role_users(),
        ]);
      } catch (error) {
        console.error('Error loading role data:', error);
      } finally {
        this.permissions_loading = false;
        this.users_loading = false;
        this.initialize_data_table();
        this.initialize_user_data_table();
      }
    },

    // ---- API (ALL VIA jQuery) ----
    get_role_details() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();

      return new Promise((resolve, reject) => {
        $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_permission_roles/`,
          method: 'GET',
          xhrFields: { withCredentials: true },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        }).done((data) => {
          if (data?.status === 'success') {
            const role = (data.data || []).find(r => Number(r.id) === this.role_id);
            if (role) {
              this.role_name = role.name || '';
              this.role_description = role.description || '';
            }
          } else {
            this.$notyf?.error(data?.message || 'Failed to fetch roles');
          }
          resolve();
        }).fail((jq) => {
          this.$notyf?.error(`Error fetching role details: HTTP ${jq.status}`);
          reject(jq);
        });
      });
    },

    get_role_permissions() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();

      return new Promise((resolve, reject) => {
        $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_role_permissions/${this.role_id}/`,
          method: 'GET',
          xhrFields: { withCredentials: true },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        }).done((data) => {
          if (data?.status === 'success') {
            const perms = (data.data || [])
              .map(this.normalize_permission_data)
              .map(p => ({ ...p, id: Number(p.id) })); // force numeric
            this.role_permissions = perms;
          } else {
            this.role_permissions = [];
          }
          resolve();
        }).fail((jq) => {
          this.$notyf?.error(`Error fetching permissions: HTTP ${jq.status}`);
          this.role_permissions = [];
          reject(jq);
        });
      });
    },

    get_all_permissions() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();

      return new Promise((resolve, reject) => {
        $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_available_permissions/`,
          method: 'GET',
          xhrFields: { withCredentials: true },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        }).done((data) => {
          if (data?.status === 'success') {
            this.available_permissions = (data.data || [])
              .map(this.normalize_permission_data)
              .map(p => ({ ...p, id: Number(p.id) })); // force numeric
          } else {
            this.available_permissions = [];
            this.$notyf?.error(data?.message || 'Failed to fetch permissions');
          }
          resolve();
        }).fail((jq) => {
          this.$notyf?.error(`Error fetching available permissions: HTTP ${jq.status}`);
          this.available_permissions = [];
          reject(jq);
        });
      });
    },

    // ---- Modal management ----
    async open_assign_modal() {
      this.show_assign_modal = true;
      this.selected_permissions = [];
      this.permission_search = '';
      this.modal_loading = true;

      // Load permissions if not already loaded
      try {
        await this.get_all_permissions();
      } catch (error) {
        console.error('Error loading permissions:', error);
      } finally {
        this.modal_loading = false;
      }
    },

    close_assign_modal() {
      this.show_assign_modal = false;
      this.selected_permissions = [];
      this.permission_search = '';
      this.returning_to_assign_modal = false;
    },

    show_permission_details(permission) {
      // If assign modal is open, remember it
      if (this.show_assign_modal) {
        this.returning_to_assign_modal = true;
        this.show_assign_modal = false;
      }

      // Ensure arrays exist
      const normalized_permission = {
        ...permission,
        main_features: Array.isArray(permission.main_features) ? permission.main_features : [],
        main_features_detailed: Array.isArray(permission.main_features_detailed) ? permission.main_features_detailed : []
      };

      this.selected_permission_details = normalized_permission;
      this.show_details_modal = true;
    },

    close_details_modal() {
      this.show_details_modal = false;
      this.selected_permission_details = null;

      // If we came from assign modal, reopen it
      if (this.returning_to_assign_modal) {
        this.$nextTick(() => {
          this.show_assign_modal = true;
          this.returning_to_assign_modal = false;
        });
      }
    },

    // ---- selection helpers ----
    is_permission_selected(permission) {
      return this.selected_permissions.some(p => Number(p.id) === Number(permission.id));
    },

    toggle_permission_selection(permission) {
      const index = this.selected_permissions.findIndex(p => Number(p.id) === Number(permission.id));
      if (index > -1) {
        this.selected_permissions.splice(index, 1);
      } else {
        this.selected_permissions.push(permission);
      }
    },

    // sanitize & dedup IDs
    sanitized_ids(arr) {
      return Array.from(
        new Set(
          (arr || [])
            .map(Number)
            .filter(n => Number.isFinite(n))
        )
      );
    },

    async assign_selected_permissions() {
      if (this.selected_permissions.length === 0) {
        this.$notyf?.info('Please select at least one permission');
        return;
      }

      const token = this.require_token_or_redirect();
      if (!token) return;

      this.modal_loading = true;

      const all_permission_ids = this.sanitized_ids([
        ...this.assigned_permission_ids,
        ...this.selected_permissions.map(p => p.id)
      ]);

      return new Promise((resolve, reject) => {
        $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/assign_permissions_to_role/`,
          method: 'POST',
          data: JSON.stringify({
            role_id: Number(this.role_id),
            permission_ids: all_permission_ids
          }),
          contentType: 'application/json',
          xhrFields: { withCredentials: true },
          headers: {
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        }).done(async (data) => {
          if (data?.status === 'success') {
            this.$notyf?.success(`${this.selected_permissions.length} permission(s) assigned successfully`);

            // Close modal
            this.close_assign_modal();

            // Refresh
            try {
              await this.load_role_data();
            } catch (error) {
              console.error('Error reloading data:', error);
            }
            resolve();
          } else {
            this.$notyf?.error(data?.message || 'Failed to assign permissions');
            reject(data);
          }
        }).fail((jq) => {
          this.$notyf?.error(`Error assigning permissions: HTTP ${jq.status}`);
          reject(jq);
        }).always(() => {
          this.modal_loading = false;
        });
      });
    },

    remove_permission(permission) {
      Swal.fire({
        title: 'Remove Permission?',
        text: `Remove "${permission.name}" from this role?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it!',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        const token = this.require_token_or_redirect();
        if (!token) return;

        const remaining_ids = this.sanitized_ids(
          this.assigned_permission_ids.filter(id => Number(id) !== Number(permission.id))
        );

        return new Promise((resolve, reject) => {
          $.ajax({
            url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/assign_permissions_to_role/`,
            method: 'POST',
            data: JSON.stringify({
              role_id: Number(this.role_id),
              permission_ids: remaining_ids
            }),
            contentType: 'application/json',
            xhrFields: { withCredentials: true },
            headers: {
              'Authorization': `Token ${token}`,
              'X-CSRFToken': this.csrfToken,
              'X-Page-URL': window.location.href
            }
          }).done(async (data) => {
            if (data?.status === 'success') {
              this.$notyf?.success('Permission removed successfully');
              try {
                await this.load_role_data();
              } catch (error) {
                console.error('Error reloading data:', error);
              }
              resolve();
            } else {
              this.$notyf?.error(data?.message || 'Failed to remove permission');
              reject(data);
            }
          }).fail((jq) => {
            this.$notyf?.error(`Error removing permission: HTTP ${jq.status}`);
            reject(jq);
          });
        });
      });
    },

    async get_role_users() {
      const token = this.require_token_or_redirect();
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_role_users?role_id=${this.role_id}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
              'X-CSRFToken': this.csrf_token,
              'X-Page-URL': window.location.href
          },
        });

        if(response.ok) {
          const data = await response.json();
          if (data?.status === 'success') this.role_users = data.data;
        } else {
          console.error('Error fetching users:', response.statusText);
        }

      }catch (error) {
          console.error('Error fetching role users:', error);
      }
    },

    async activate_user(user) {
      const token = get_token();
      if (!token) {
          this.$router.push({ name: 'Login' });
          return;
        }
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/activate_user/`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href,

          },
          body: JSON.stringify({ user_id: user.id }),
        });
        const data = await response.json();
        if (response.ok) {
          user.is_active = true;
          this.$notyf.success('User activated successfully.');
        } else {
          const msg = data?.message || `Failed to activate user: HTTP ${response.status}`;
          this.$notyf.error(msg);
        }
      } catch (error) {
        this.$notyf.error(`Error: ${String(error)}`);
      }
    },

    async deactivate_user(user) {
      const token = get_token();
      if (!token) {
          this.$router.push({ name: 'Login' });
          return;
        }
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/deactivate_user/`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href,

          },
          body: JSON.stringify({ user_id: user.id }),
        });
        const data = await response.json();
        if (response.ok) {
          user.is_active = false;
          this.$notyf.success('User deactivated successfully.');
        } else {
          const msg = data?.message || `Failed to deactivate user: HTTP ${response.status}`;
          this.$notyf.error(msg);
        }
      } catch (error) {
        this.$notyf.error(`Error: ${String(error)}`);
      }
    },

    truncate_text(text, length = 50) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    }
  }
};